/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { action, observable } from "mobx";
import CommonPopup from "Components/common/CommonPopup";
import makeApi, { URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import "./enrichPopup.scss";

/**
 * Lets other parts of the Contacts page (e.g. the post-import prompt) open the
 * Enrich popup for an explicit set of contact ids. ContactModule renders it.
 */
class EnrichPromptState {
  @observable show = false;
  @observable contactIds = [];

  @action
  open = (contactIds = []) => {
    this.contactIds = contactIds;
    this.show = contactIds.length > 0;
  };

  @action
  close = () => {
    this.show = false;
    this.contactIds = [];
  };
}

export const enrichPromptState = new EnrichPromptState();

const OPTIONS_STORAGE_KEY = "sg_enrich_popup_options";
const DEFAULT_OPTIONS = { profile: false, email: true, phone: false };

const readSavedOptions = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(OPTIONS_STORAGE_KEY));
    return saved && typeof saved === "object"
      ? { ...DEFAULT_OPTIONS, ...saved }
      : { ...DEFAULT_OPTIONS };
  } catch (e) {
    return { ...DEFAULT_OPTIONS };
  }
};

const saveOptions = options => {
  try {
    localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(options));
  } catch (e) {
    // localStorage may be unavailable (private mode); the popup still works
  }
};

// Maps the checkbox selection onto the bulk-op payload the backend expects.
export const buildEnrichContactPayload = ({ profile, email, phone }) => {
  let enrichType = "PROFILE";
  if (email && phone) enrichType = "EMAIL_PHONE";
  else if (email) enrichType = "EMAIL";
  else if (phone) enrichType = "PHONE";
  return { enrichType, profile: Boolean(profile) };
};

const fmt = n => Utils.numberWithCommas(n || 0);

// How Data Toolkit results update the contacts they ran on.
export const UPDATE_MODES = [
  { value: "FILL_EMPTY", label: "Fill empty fields only" },
  { value: "OVERWRITE", label: "Overwrite existing values" }
];

// What the popup runs. Enrich is the bulk enrich job; the other two start their own Data Toolkit job.
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round"
};
const MODE_ICONS = {
  enrich: (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      aria-hidden="true"
      {...stroke}
    >
      <path d="M10 2.5l1.6 4.4 4.4 1.6-4.4 1.6L10 14.5l-1.6-4.4L4 8.5l4.4-1.6z" />
      <path d="M15.5 13.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6z" />
    </svg>
  ),
  validate: (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      aria-hidden="true"
      {...stroke}
    >
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path d="M3 5.5l7 5 7-5" />
      <path d="M12.5 15.5l1.8 1.8 3.4-3.6" />
    </svg>
  ),
  people: (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      aria-hidden="true"
      {...stroke}
    >
      <circle cx="7.5" cy="7" r="3" />
      <path d="M2.5 16.5c.6-2.6 2.6-4 5-4s4.4 1.4 5 4" />
      <path d="M13 4.2a3 3 0 010 5.6M14.5 12.7c1.5.5 2.6 1.8 3 3.8" />
    </svg>
  ),
  dataMagic: (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      aria-hidden="true"
      {...stroke}
    >
      <path d="M3.5 16.5l9-9" />
      <path d="M11 6l3 3" />
      <path d="M14.5 2.5v2M13.5 3.5h2M17 7v1.6M16.2 7.8h1.6M8 3v1.4M7.3 3.7h1.4" />
    </svg>
  )
};

// What the popup can run, one at a time. The intro says what each does, so nobody needs to know
// what the Data Toolkit is.
const MODES = [
  {
    id: "enrich",
    label: "Enrich",
    intro:
      "Fill in the latest details. Runs the profile first, then emails and phones."
  },
  {
    id: "validate",
    label: "Validate emails",
    tool: "VALIDATE_EMAIL",
    intro:
      "Checks each email and saves the result on the contact. Runs in the background."
  },
  {
    id: "dataMagic",
    label: "Clean up",
    tool: "DATA_MAGIC",
    intro:
      "Fixes names and companies, catches job changes and finds the right email. Runs in the background."
  }
];

/** A small on/off switch for one row. */
const Switch = ({ on, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={Boolean(on)}
    aria-label={label}
    disabled={disabled}
    className={`enrichSwitch ${on ? "on" : ""}`}
    onClick={() => !disabled && onChange(!on)}
  />
);
const credits = n => `${fmt(n)} ${n === 1 ? "credit" : "credits"}`;

/**
 * Enrich popup. A switch at the top picks what runs on the selected contacts — one at a time. The
 * body keeps one height for all three, so the popup doesn't jump when you switch.
 *  - Enrich (default): one row per step (profile / emails / phones) with counts, run as the bulk
 *    enrich job.
 *  - Validate emails / Clean up (Data magic): each starts its own Data Toolkit job on the
 *    selection, which updates the contacts when it finishes (always replacing — all or nothing,
 *    like Enrich). Only offered where the page passes onToolkitSubmit (the Contacts page).
 *
 * props:
 *  - selectedCount: number of contacts the user selected
 *  - getPreviewPayload(): the same {search, filter, totalCount} payload the run uses
 *  - onSubmit(enrichContactPayload): starts the enrich run
 *  - onToolkitSubmit({tool, updateMode, findAlternates}) (optional): starts a Data Toolkit job
 *  - onClose()
 */
const EnrichPopup = props => {
  const {
    selectedCount: pageSelectedCount = 0,
    getPreviewPayload = () => ({}),
    onSubmit = () => {},
    onToolkitSubmit = null,
    onClose = () => {}
  } = props || {};
  const toolkitOn = typeof onToolkitSubmit === "function";
  // The selection's real size: with "select all", the page's checked rows undercount it
  // (10 on screen vs 4,238 selected), but the run payload carries the full total.
  const [selectedCount] = useState(() => {
    const total = Number(getPreviewPayload()?.totalCount);
    return total > 0 ? total : pageSelectedCount;
  });

  const [mode, setMode] = useState("enrich");
  const [options, setOptions] = useState(readSavedOptions);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Data Toolkit modes
  const [findAlternates, setFindAlternates] = useState(false);
  const [toolPreview, setToolPreview] = useState(null);
  const [toolLoading, setToolLoading] = useState(false);

  const activeMode = MODES.find(m => m.id === mode) || MODES[0];
  const isEnrich = activeMode.id === "enrich";

  const fetchPreview = async () => {
    setLoading(true);
    const config = {
      url: URLS.enrichPreview,
      method: "POST",
      data: getPreviewPayload()
    };
    const response = await makeApi(config);
    setPreview(response?.data || null);
    setLoading(false);
  };

  useEffect(() => {
    fetchPreview();
  }, []);

  // The Data Toolkit's own count and cost for the selection (its limits, not Enrich's).
  useEffect(() => {
    if (isEnrich) return;
    let cancelled = false;
    (async () => {
      setToolLoading(true);
      const selection = getPreviewPayload() || {};
      const response = await makeApi({
        url: URLS.dataToolkitContactsPreview,
        method: "POST",
        data: {
          tool: activeMode.tool,
          search: selection.search,
          contactIds: selection.filter?.contactIds,
          updateMode: "OVERWRITE",
          findAlternates: activeMode.id === "dataMagic" && findAlternates
        }
      });
      if (!cancelled) {
        setToolPreview(response?.data || null);
        setToolLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, findAlternates]);

  const chooseMode = id => {
    setMode(id);
    setToolPreview(null);
  };

  const toggle = (checked, id) => {
    const next = { ...options, [id]: checked };
    setOptions(next);
    saveOptions(next);
  };

  const {
    total = selectedCount,
    missingEmail = 0,
    missingPhone = 0,
    profileEligible = 0,
    remainingCredits = 0,
    creditsPerEmail = 1,
    creditsPerPhone = 10,
    phoneEnabled = true,
    bulkEnrichLimit = 0,
    truncated = false
  } = preview || {};

  // ── Enrich mode (unchanged) ──
  // One credit pool: profile and email cost creditsPerEmail each, phone creditsPerPhone.
  const profileCredits = profileEligible * creditsPerEmail;
  const emailCredits = missingEmail * creditsPerEmail;
  const phoneCredits = missingPhone * creditsPerPhone;
  const enrichCredits =
    (options.profile ? profileCredits : 0) +
    (options.email ? emailCredits : 0) +
    (options.phone && phoneEnabled ? phoneCredits : 0);
  const nothingSelected =
    !options.profile && !options.email && !(options.phone && phoneEnabled);
  const enrichOverLimit =
    truncated || (bulkEnrichLimit > 0 && selectedCount > bulkEnrichLimit);

  // ── Data Toolkit modes ──
  const toolTotal = toolPreview?.total ?? total;
  const toolUsable = toolPreview?.usable || 0;
  const toolCredits = toolPreview?.maxCredits || 0;
  const toolRemaining = toolPreview?.remainingCredits ?? remainingCredits;
  const toolOverLimit = Boolean(toolPreview?.truncated);

  const busy = isEnrich ? loading : toolLoading || !toolPreview;
  const creditsNeeded = isEnrich ? enrichCredits : toolCredits;
  const available = isEnrich ? remainingCredits : toolRemaining;
  const overLimit = isEnrich ? enrichOverLimit : toolOverLimit;
  const notEnoughCredits = creditsNeeded > available;
  const nothingToRun = isEnrich ? nothingSelected : toolUsable === 0;
  const canSubmit =
    !busy && !submitting && !nothingToRun && !overLimit && !notEnoughCredits;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isEnrich) {
        await onSubmit(
          buildEnrichContactPayload({
            ...options,
            phone: options.phone && phoneEnabled
          })
        );
      } else {
        await onToolkitSubmit({
          tool: activeMode.tool,
          updateMode: "OVERWRITE",
          findAlternates: activeMode.id === "dataMagic" && findAlternates
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const rows = [
    {
      id: "profile",
      title: "Update profile",
      description: "Title, company, location. Follows job changes.",
      count: profileEligible,
      countHint: "can be looked up"
    },
    {
      id: "email",
      title: "Find missing emails",
      description: "None, bounced, or from an old job",
      count: missingEmail,
      countHint: "have no email or a bad one"
    },
    {
      id: "phone",
      title: "Find missing phones",
      description: phoneEnabled
        ? `${creditsPerPhone} credits per number found`
        : "Not included in your plan",
      count: missingPhone,
      countHint: "have no phone",
      disabled: !phoneEnabled
    }
  ];

  const getBlockingMessage = () => {
    if (busy) return "";
    if (overLimit) {
      return isEnrich
        ? `Up to ${fmt(bulkEnrichLimit)} contacts per run. Narrow your selection.`
        : `Up to ${fmt(toolPreview?.maxContacts)} contacts per run. Narrow your selection.`;
    }
    if (notEnoughCredits) {
      return `Needs up to ${fmt(creditsNeeded)}, you have ${fmt(available)}.`;
    }
    if (!isEnrich && nothingToRun) {
      return activeMode.id === "validate"
        ? "None of the selected contacts has an email to validate."
        : "None of the selected contacts has enough details to look up.";
    }
    return "";
  };
  const blockingMessage = getBlockingMessage();

  const count = `${fmt(selectedCount)} ${selectedCount === 1 ? "contact" : "contacts"}`;
  const verb = {
    enrich: "Enrich",
    validate: "Validate",
    dataMagic: "Clean up"
  }[activeMode.id];

  const counting = isEnrich ? loading : toolLoading || !toolPreview;

  const renderEnrich = () => (
    <div className="enrichGroup">
      {rows.map(row => (
        <div
          key={row.id}
          className={`enrichRow ${row.disabled ? "disabled" : ""}`}
        >
          <Switch
            label={row.title}
            on={options[row.id] && !row.disabled}
            disabled={row.disabled}
            onChange={on => toggle(on, row.id)}
          />
          <div
            className="enrichRowText"
            onClick={() => !row.disabled && toggle(!options[row.id], row.id)}
          >
            <span className="enrichRowTitle">{row.title}</span>
            <span className="enrichRowDesc">{row.description}</span>
          </div>
          <span
            className="enrichRowCount"
            title={`${fmt(row.count)} of ${fmt(total)} ${row.countHint}`}
          >
            {counting ? "…" : overLimit ? "–" : `${fmt(row.count)} of ${fmt(total)}`}
          </span>
        </div>
      ))}
    </div>
  );

  const renderValidate = () => (
    <>
      <div className="enrichStatuses">
        <span className="enrichStatus good">Valid</span>
        <span className="enrichStatus warn">Risky</span>
        <span className="enrichStatus bad">Invalid</span>
      </div>
      <div className="enrichGroup">
        <div className="enrichRow">
          <span className="enrichRowIcon">{MODE_ICONS.validate}</span>
          <div className="enrichRowText">
            <span className="enrichRowTitle">
              {counting
                ? "Counting…"
                : overLimit
                  ? `${fmt(toolTotal)} selected`
                  : `${fmt(toolUsable)} of ${fmt(toolTotal)} have an email to check`}
            </span>
            <span className="enrichRowDesc">
              1 credit per email · contacts without one are skipped
            </span>
          </div>
        </div>
      </div>
    </>
  );

  const renderCleanUp = () => (
    <div className="enrichGroup">
      <div className="enrichRow">
        <span className="enrichRowIcon">{MODE_ICONS.people}</span>
        <div className="enrichRowText">
          <span className="enrichRowTitle">
            {counting
              ? "Counting…"
              : overLimit
                ? `${fmt(toolTotal)} selected`
                : `${fmt(toolUsable)} of ${fmt(toolTotal)} can be looked up`}
          </span>
          <span className="enrichRowDesc">
            Up to 3 credits each: contact, company (once per company), new email
          </span>
        </div>
      </div>
      <div className="enrichRow">
        <Switch
          label="Find a replacement"
          on={findAlternates}
          onChange={setFindAlternates}
        />
        <div
          className="enrichRowText"
          onClick={() => setFindAlternates(!findAlternates)}
        >
          <span className="enrichRowTitle">Find a replacement</span>
          <span className="enrichRowDesc">
            When someone has moved or can't be reached · 2 credits each
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <CommonPopup
      className="enrichPopup"
      headerTitle={`${verb} ${count}`}
      closeIconCbk={onClose}
    >
      <div className="enrichPopupBody">
        {toolkitOn && (
          <div
            className="enrichModes"
            role="radiogroup"
            aria-label="What to run"
          >
            {MODES.map(m => (
              <button
                type="button"
                key={m.id}
                role="radio"
                aria-checked={mode === m.id}
                className={`enrichMode ${mode === m.id ? "selected" : ""}`}
                onClick={() => chooseMode(m.id)}
              >
                {MODE_ICONS[m.id]}
                <span>{m.label}</span>
              </button>
            ))}
          </div>
        )}
        <div className={`enrichPane ${toolkitOn ? "fixed" : ""}`}>
          <p className="enrichPopupHint">{activeMode.intro}</p>
          {isEnrich
            ? renderEnrich()
            : activeMode.id === "validate"
              ? renderValidate()
              : renderCleanUp()}
        </div>
        <div className={`enrichFooter ${blockingMessage ? "hasError" : ""}`}>
          <div className="enrichFooterInfo">
            {busy ? (
              // Same two lines as the loaded footer, so the popup keeps its height while counting.
              <>
                <span className="enrichFooterPrimary muted">
                  Calculating credits…
                </span>
                <span className="enrichFooterSecondary">&nbsp;</span>
              </>
            ) : blockingMessage ? (
              <>
                <span className="enrichFooterPrimary error">
                  <i className="material-icons-outlined">error_outline</i>
                  {overLimit
                    ? "Selection too large"
                    : notEnoughCredits
                      ? "Not enough credits"
                      : "Nothing to run"}
                </span>
                <span className="enrichFooterSecondary">{blockingMessage}</span>
              </>
            ) : (
              <>
                <span className="enrichFooterPrimary">
                  Uses up to {credits(creditsNeeded)}
                </span>
                <span className="enrichFooterSecondary">
                  {fmt(available)} available · charged only when found
                </span>
              </>
            )}
          </div>
          <div className="enrichFooterActions">
            <button
              type="button"
              className="enrichCancelBtn"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`enrichSubmitBtn ${submitting ? "loading" : ""}`}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting
                ? "Starting…"
                : isEnrich
                  ? `Enrich ${count}`
                  : `${verb} ${fmt(toolUsable)} ${toolUsable === 1 ? "contact" : "contacts"}`}
            </button>
          </div>
        </div>
      </div>
    </CommonPopup>
  );
};

export { EnrichPopup };
export default observer(EnrichPopup);
